import { type CountryCode } from 'libphonenumber-js/mobile'
import type React from 'react'
import { useEffect, useRef, useState } from 'react'
import {
  Pressable,
  // eslint-disable-next-line @typescript-eslint/no-restricted-imports
  Text,
  TextInput,
  type TextInputProps,
  View,
} from 'react-native'
import { PhoneInputField } from '~/components/ui/PhoneInputField'
import designTokens from '~/lib/designTokens'
import { cn } from '~/lib/utils'
import Icon from '../Icon'

export type InputVariant = 'box' | 'line' | 'line-dark'

export type InputProps = Omit<TextInputProps, 'value' | 'onChangeText'> & {
  ref?: React.Ref<TextInput | null>
  showClearButton?: boolean
  formatted?: boolean
  value?: string | number
  thousandSeparator?: boolean | string
  decimalSeparator?: string
  decimalScale?: number
  fixedDecimalScale?: boolean
  allowNegative?: boolean
  prefix?: string
  suffix?: string
  inputClassName?: string
  variant?: InputVariant
  labelsClassName?: string
  error?: string | null
} & (
    | {
        type?: 'text' | 'numeric'
        onChangeText?: (text: string) => void
      }
    | {
        type: 'phone'
        onChangeText: (
          displayPhoneNumber: string,
          countryCode: CountryCode,
          storedPhoneNumber: string,
          isValid: boolean
        ) => void
        countrySelectClassName?: string
      }
  )

const Input = ({
  className,
  placeholderClassName,
  showClearButton = false,
  type = 'text',
  formatted = true,
  value,
  onChangeText,
  thousandSeparator,
  decimalScale,
  fixedDecimalScale,
  prefix,
  suffix,
  inputClassName,
  variant = 'line',
  ref: propRef,
  labelsClassName,
  error,
  onBlur: propOnBlur,
  ...props
}: InputProps) => {
  const innerRef = useRef<TextInput>(null)
  const ref = propRef ?? innerRef

  const [touched, setTouched] = useState(false)
  const showError = touched && !!error

  const handleBlur: NonNullable<TextInputProps['onBlur']> = (e) => {
    setTouched(true)
    propOnBlur?.(e)
  }

  const inputBorderClasses = cn(
    'relative flex-1 flex-row items-center gap-2',
    variant === 'line' && 'border-b-2 border-tertiary-foreground bg-transparent',
    variant === 'line-dark' && 'border-b-2 border-secondary bg-transparent',
    variant === 'box' &&
      'native:shadow-card native:shadow-secondary/30 web:shadow web:shadow-primary/20 rounded-xl border border-tertiary-foreground bg-background px-3 py-1',
    props.editable === false && 'web:cursor-not-allowed',
    props.multiline && 'items-start',
    showError && 'border-destructive'
  )

  const inputFieldClassName = cn(
    'native:leading-snug web:px-0 web:leading-loose web:placeholder:text-foreground/50 web:focus-visible:outline-none flex min-w-0 flex-1 border-0 bg-transparent px-0 py-0 font-normal text-lg/normal text-foreground file:border-0 file:bg-transparent file:font-medium',
    props.multiline ? 'min-h-10' : 'h-10',
    inputClassName
  )

  const placeholderClass = cn('text-foreground/50', placeholderClassName)

  const hasValue = value !== undefined && value !== null && String(value).length > 0
  const canClear = showClearButton && hasValue && props.editable !== false

  const handleClear = () => {
    onChangeText?.('', 'FI', '', true)
  }

  const renderClearButton = () => {
    if (!showClearButton) return null

    return (
      <Pressable
        onPress={handleClear}
        disabled={!canClear}
        className={cn(
          'size-9 items-center justify-center',
          canClear ? 'opacity-70' : 'opacity-30'
        )}>
        <Icon name="close" size={16} color={canClear ? designTokens.foreground : '#d1d5db'} />
      </Pressable>
    )
  }

  const textStyle = cn('py-2 font-semibold text-lg text-foreground', labelsClassName)

  let content = null

  const userUpdatedNumericValue = useRef(false)
  const [internalNumericValue, setInternalNumericValue] = useState<string>(String(value ?? ''))

  useEffect(() => {
    if (!userUpdatedNumericValue.current) {
      const nextInternalValue = String(value ?? '')

      setInternalNumericValue((current) => {
        if (nextInternalValue !== current) {
          return nextInternalValue
        }

        return current
      })
    }

    return () => {
      userUpdatedNumericValue.current = false
    }
  }, [value])

  const changeHandler = onChangeText as ((text: string) => void) | undefined

  const formatNumber = (val: string): string => {
    if (!val.trim()) return ''

    if (val.endsWith('.')) {
      return val
    }

    const num = Number(val)

    if (!Number.isFinite(num)) {
      return ''
    }

    const options: Intl.NumberFormatOptions = {
      useGrouping: !!thousandSeparator,
      style: 'decimal',
    }

    if (decimalScale !== undefined) {
      options.maximumFractionDigits = decimalScale

      if (fixedDecimalScale) {
        options.minimumFractionDigits = decimalScale
      }
    }

    // TODO: use the locale from the user language settings.
    return new Intl.NumberFormat('fi-FI', options).format(num)
  }

  const handleNumericChange = (text: string) => {
    const cleaned = text.replace(/[^0-9.,]/g, '')
    const normalized = cleaned.replace(',', '.')

    if (changeHandler && !normalized.endsWith('.')) {
      changeHandler(cleaned)
    }

    const displayValue = formatted ? formatNumber(normalized) : normalized
    userUpdatedNumericValue.current = true
    setInternalNumericValue(displayValue)
  }

  if (type === 'numeric') {
    content = (
      <>
        {prefix ? <Text className={textStyle}>{prefix}</Text> : null}
        <TextInput
          ref={ref}
          className={cn(inputFieldClassName)}
          placeholderClassName={placeholderClass}
          value={internalNumericValue}
          onChangeText={handleNumericChange}
          keyboardType="numeric"
          selectTextOnFocus
          onBlur={handleBlur}
          {...props}
        />
        {suffix ? <Text className={textStyle}>{suffix}</Text> : null}
        {renderClearButton()}
      </>
    )
  } else if (type === 'phone') {
    content = (
      <>
        {prefix ? <Text className={textStyle}>{prefix}</Text> : null}
        <PhoneInputField
          ref={ref}
          {...props}
          value={String(value)}
          onChange={onChangeText as (text: string) => void}
          className={cn(inputFieldClassName)}
          countrySelectClassName={cn(
            variant === 'box' && 'ml-0',
            (props as Extract<InputProps, { type: 'phone' }>).countrySelectClassName
          )}
          placeholderClassName={placeholderClass}
          textAlignVertical="center"
          keyboardType="phone-pad"
          onBlur={handleBlur}>
          {renderClearButton()}
        </PhoneInputField>
        {suffix ? <Text className={textStyle}>{suffix}</Text> : null}
      </>
    )
  } else {
    content = (
      <>
        {prefix ? <Text className={textStyle}>{prefix}</Text> : null}
        <TextInput
          ref={ref}
          className={cn(inputFieldClassName)}
          placeholderClassName={placeholderClass}
          value={String(value)}
          onChangeText={onChangeText as (text: string) => void}
          textAlignVertical="center"
          keyboardType="default"
          onBlur={handleBlur}
          {...props}
        />
        {suffix ? <Text className={textStyle}>{suffix}</Text> : null}
        {renderClearButton()}
      </>
    )
  }

  const onPressFocus = () => {
    if (typeof ref === 'object' && ref !== null && 'current' in ref && ref.current) {
      ref.current?.focus()
    }
  }

  if (error !== undefined) {
    return (
      <View className={cn('gap-1', className)}>
        <Pressable
          tabIndex={-1}
          onPress={onPressFocus}
          className={cn(inputBorderClasses, 'pointer-events-auto')}>
          {content}
        </Pressable>
        {showError && <Text className="text-xs text-destructive">{error}</Text>}
      </View>
    )
  }

  return (
    <Pressable
      tabIndex={-1}
      onPress={onPressFocus}
      className={cn(inputBorderClasses, className, 'pointer-events-auto')}>
      {content}
    </Pressable>
  )
}

export { Input }
